import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Form, FormFeedback, Input, Label, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "auth/useAuth";
import { translateAuthError } from "auth/cognitoClient";
import { getPendingConfirmation } from "auth/pendingState";
import { track, EVENTS } from "telemetry/track";
import AuthLayout from "./AuthLayout";

const PASSWORD_RULES = "Mínimo de 8 caracteres, com letra maiúscula, minúscula, número e símbolo.";

const SignIn = () => {
  document.title = "Entrar | ACIG Guapiaçu Cidadão";

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("credentials"); // credentials | newPassword
  const [submitError, setSubmitError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // "/" lets the role-aware Home decide (admin → painel, others → perfil).
  const from = location.state?.from || "/";
  const prefilledEmail = location.state?.email || "";
  const flash = location.state?.flash || null; // e.g. "Conta confirmada! Entre para continuar."
  // A sign-up that never got confirmed (e.g. the tab was killed while the user
  // fetched the code) — offer to resume instead of leaving them stuck.
  const pendingConfirmation = getPendingConfirmation();

  const credentials = useFormik({
    enableReinitialize: true,
    initialValues: { email: prefilledEmail, password: "" },
    validationSchema: Yup.object({
      email: Yup.string().trim().email("Informe um e-mail válido.").required("Informe seu e-mail."),
      password: Yup.string().required("Informe sua senha."),
    }),
    onSubmit: async ({ email, password }) => {
      setSubmitError(null);
      try {
        const result = await auth.signIn(email, password);
        if (result.status === "SIGNED_IN") track(EVENTS.LOGIN_SUCCESS);
        if (result.status === "SIGNED_IN") navigate(from, { replace: true });
        else if (result.status === "NEW_PASSWORD_REQUIRED") setStep("newPassword");
        else if (result.status === "UNCONFIRMED") navigate("/confirmar-conta", { state: { email: result.email } });
      } catch (err) {
        console.error("Sign-in failed:", err);
        track(EVENTS.LOGIN_FAILED, { code: err?.code || "unknown" });
        if (err?.code === "PasswordResetRequiredException") {
          navigate("/esqueci-senha", {
            state: { email: email.trim().toLowerCase(), notice: "Por segurança, você precisa redefinir sua senha antes de entrar." },
          });
          return;
        }
        setSubmitError(translateAuthError(err));
      }
    },
  });

  const newPassword = useFormik({
    initialValues: { password: "", confirm: "" },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, PASSWORD_RULES)
        .matches(/[a-z]/, PASSWORD_RULES)
        .matches(/[A-Z]/, PASSWORD_RULES)
        .matches(/\d/, PASSWORD_RULES)
        .matches(/[^A-Za-z0-9]/, PASSWORD_RULES)
        .required("Informe a nova senha."),
      confirm: Yup.string()
        .oneOf([Yup.ref("password")], "As senhas não coincidem.")
        .required("Confirme a nova senha."),
    }),
    onSubmit: async ({ password }) => {
      setSubmitError(null);
      try {
        await auth.completeNewPassword(password);
        navigate(from, { replace: true });
      } catch (err) {
        console.error("New password failed:", err);
        setSubmitError(translateAuthError(err));
      }
    },
  });

  useEffect(() => { setSubmitError(null); }, [step]);

  // Already signed in (e.g. back button) → go straight to the app.
  if (!auth.isLoading && auth.isAuthenticated) return <Navigate to={from} replace />;

  if (step === "newPassword") {
    return (
      <AuthLayout title="Defina sua nova senha" subtitle="Seu acesso foi criado com uma senha temporária.">
        <Form onSubmit={newPassword.handleSubmit} noValidate>
          {submitError && <Alert color="danger">{submitError}</Alert>}
          <p className="text-muted small">{PASSWORD_RULES}</p>
          <div className="mb-3">
            <Label htmlFor="np-password">Nova senha</Label>
            <Input id="np-password" name="password" type="password" autoComplete="new-password"
              value={newPassword.values.password} onChange={newPassword.handleChange} onBlur={newPassword.handleBlur}
              invalid={!!(newPassword.touched.password && newPassword.errors.password)} />
            <FormFeedback>{newPassword.errors.password}</FormFeedback>
          </div>
          <div className="mb-3">
            <Label htmlFor="np-confirm">Confirmar nova senha</Label>
            <Input id="np-confirm" name="confirm" type="password" autoComplete="new-password"
              value={newPassword.values.confirm} onChange={newPassword.handleChange} onBlur={newPassword.handleBlur}
              invalid={!!(newPassword.touched.confirm && newPassword.errors.confirm)} />
            <FormFeedback>{newPassword.errors.confirm}</FormFeedback>
          </div>
          <Button color="primary" block type="submit" disabled={newPassword.isSubmitting}>
            {newPassword.isSubmitting && <Spinner size="sm" className="me-2" />}
            Salvar e entrar
          </Button>
        </Form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Bem-vindo(a) de volta!"
      subtitle="Entre para acessar seu perfil e currículo."
      footer={
        <p className="mb-0">
          Ainda não tem uma conta?{" "}
          <Link to="/cadastro" className="fw-medium text-primary">Cadastre-se</Link>
        </p>
      }
    >
      <Form onSubmit={credentials.handleSubmit} noValidate>
        {flash && <Alert color="success">{flash}</Alert>}
        {submitError && <Alert color="danger">{submitError}</Alert>}
        {pendingConfirmation?.email && !flash && (
          <Alert color="warning" className="small">
            Seu cadastro com <strong>{pendingConfirmation.email}</strong> ainda não foi confirmado.{" "}
            <Link to="/confirmar-conta" state={{ email: pendingConfirmation.email }} className="alert-link">
              Confirmar agora
            </Link>
          </Alert>
        )}

        <div className="mb-3">
          <Label htmlFor="login-email">E-mail</Label>
          <Input id="login-email" name="email" type="email" inputMode="email" autoComplete="username"
            placeholder="voce@exemplo.com.br" autoFocus={!prefilledEmail}
            value={credentials.values.email} onChange={credentials.handleChange} onBlur={credentials.handleBlur}
            invalid={!!(credentials.touched.email && credentials.errors.email)} />
          <FormFeedback>{credentials.errors.email}</FormFeedback>
        </div>

        <div className="mb-3">
          <Label htmlFor="login-password">Senha</Label>
          <div className="input-group auth-pass-inputgroup">
            <Input id="login-password" name="password" type={showPassword ? "text" : "password"}
              autoComplete="current-password" placeholder="Sua senha" autoFocus={!!prefilledEmail}
              value={credentials.values.password} onChange={credentials.handleChange} onBlur={credentials.handleBlur}
              invalid={!!(credentials.touched.password && credentials.errors.password)} />
            <button type="button" className="btn btn-light" onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}>
              <i className={`mdi ${showPassword ? "mdi-eye-off-outline" : "mdi-eye-outline"}`} />
            </button>
            <FormFeedback>{credentials.errors.password}</FormFeedback>
          </div>
        </div>

        <div className="mt-3 d-grid">
          <Button color="primary" type="submit" disabled={credentials.isSubmitting}>
            {credentials.isSubmitting && <Spinner size="sm" className="me-2" />}
            Entrar
          </Button>
        </div>

        <div className="mt-4 text-center">
          <Link to="/esqueci-senha" className="text-muted">
            <i className="mdi mdi-lock me-1" /> Esqueci minha senha
          </Link>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default SignIn;
